import { useEffect, useMemo, useRef } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { filterDefaultValues } from 'components/configured-service/CreateDraftConfiguredServiceDialog/filterSettings'
import {
  UNITS,
  metersToUnit,
  unitToMeters,
  snapUnitValue,
  getSliderConfig,
  ensureCanonicalMeters,
} from 'utils/distance'

/**
 * Centralizes radius/unit behavior across Filters panel, Map, and API layer.
 * - Canonical storage is meters
 * - `radius` (live) updates immediately for map circle
 * - `radiusCommitted` updates on commit for API fetching
 * - `radiusUnit` persists the current UI unit in the form
 */
export default function useRadiusUnit({
  distanceUnitProp = UNITS.MI,
  radiusMinMeters = 10000,
  radiusMaxMeters = 50000,
  radiusStepMeters = 5000,
} = {}) {
  const { control, setValue, getValues } = useFormContext()

  // Form fields
  const radiusUnit = useWatch({ control, name: 'radiusUnit', defaultValue: getValues('radiusUnit') })
  const radius = useWatch({ control, name: 'radius', defaultValue: getValues('radius') })
  const radiusCommitted = useWatch({ control, name: 'radiusCommitted', defaultValue: getValues('radiusCommitted') })

  const unit = radiusUnit || distanceUnitProp || UNITS.MI
  const sliderConfig = useMemo(
    () => getSliderConfig(unit, { radiusMinMeters, radiusMaxMeters, radiusStepMeters }),
    [unit, radiusMinMeters, radiusMaxMeters, radiusStepMeters]
  )

  // Seed radiusUnit from prop on first mount if not present
  useEffect(() => {
    if (!radiusUnit && distanceUnitProp) {
      setValue('radiusUnit', distanceUnitProp, { shouldValidate: false, shouldDirty: false, shouldTouch: false })
    }
  }, [radiusUnit, distanceUnitProp, setValue])

  const hasInitializedRef = useRef(false)
  // Initialization and unit-change handling
  useEffect(() => {
    if (unit === UNITS.KM) {
      // In km mode, just ensure meters are integers; do not force commit to change
      const meters = Math.max(0, Math.round(Number(radius) || 0))
      if ((Number(radius) || 0) !== meters) {
        setValue('radius', meters, { shouldValidate: false, shouldDirty: false, shouldTouch: false })
      }
      if (!hasInitializedRef.current) {
        // Seed committed once on initial load
        setValue('radiusCommitted', meters, { shouldValidate: false, shouldDirty: false, shouldTouch: false })
        hasInitializedRef.current = true
      }
      return
    }

    // Miles: snap to allowed steps and keep meters canonical
    const snappedMeters = ensureCanonicalMeters(radius, unit, sliderConfig)
    if ((Math.round(Number(radius) || 0)) !== snappedMeters) {
      setValue('radius', snappedMeters, { shouldValidate: false, shouldDirty: false, shouldTouch: false })
    }
    if (!hasInitializedRef.current) {
      setValue('radiusCommitted', snappedMeters, { shouldValidate: false, shouldDirty: false, shouldTouch: false })
      hasInitializedRef.current = true
    }
  }, [unit, sliderConfig, radius, setValue])

  // Safety: if external resets change radius in miles, resnap live only
  useEffect(() => {
    if (unit !== UNITS.MI) return
    const snapped = ensureCanonicalMeters(radius, unit, sliderConfig)
    if ((Math.round(Number(radius) || 0)) !== snapped) {
      setValue('radius', snapped, { shouldValidate: false, shouldDirty: false, shouldTouch: false })
    }
  }, [unit, sliderConfig, radius, setValue])

  const toMeters = val => Math.max(0, Math.round(unitToMeters(val, unit)))
  const fromMeters = val => metersToUnit(val || 0, unit)

  const displayUnitValue = useMemo(() => {
    const unitVal = fromMeters(radius || 0)
    return unit === UNITS.MI ? snapUnitValue(unitVal, sliderConfig) : Math.round(unitVal)
  }, [unit, sliderConfig, radius])

  const onSliderChange = (eventOrValue, nextValMaybe) => {
    const rawVal = Array.isArray(eventOrValue)
      ? eventOrValue[0]
      : typeof nextValMaybe === 'number'
      ? nextValMaybe
      : Array.isArray(nextValMaybe)
      ? nextValMaybe[0]
      : eventOrValue?.target?.value ?? eventOrValue

    let unitVal = Number(rawVal) || 0
    if (unit === UNITS.MI) unitVal = snapUnitValue(unitVal, sliderConfig)
    const meters = toMeters(unitVal)
    setValue('radius', meters)
  }

  const onSliderCommit = (eventOrValue, nextValMaybe) => {
    const rawVal = Array.isArray(eventOrValue)
      ? eventOrValue[0]
      : typeof nextValMaybe === 'number'
      ? nextValMaybe
      : Array.isArray(nextValMaybe)
      ? nextValMaybe[0]
      : eventOrValue?.target?.value ?? eventOrValue

    let unitVal = Number(rawVal) || 0
    if (unit === UNITS.MI) unitVal = snapUnitValue(unitVal, sliderConfig)
    const meters = toMeters(unitVal)
    setValue('radiusCommitted', meters)
  }

  const resetRadius = () => {
    const baseMeters = Number(filterDefaultValues.radius) || 0
    const baseUnit = unit
    const unitVal = metersToUnit(baseMeters, baseUnit)
    const snappedUnitVal = baseUnit === UNITS.MI ? snapUnitValue(unitVal, sliderConfig) : Math.round(unitVal)
    const meters = Math.max(0, Math.round(unitToMeters(snappedUnitVal, baseUnit)))
    setValue('radiusUnit', baseUnit, { shouldValidate: false, shouldDirty: false, shouldTouch: false })
    setValue('radius', meters, { shouldValidate: false, shouldDirty: false, shouldTouch: false })
    setValue('radiusCommitted', meters, { shouldValidate: false, shouldDirty: false, shouldTouch: false })
  }

  const setUnit = nextUnit => {
    if (!nextUnit || nextUnit === unit) return
    setValue('radiusUnit', nextUnit, { shouldValidate: false, shouldDirty: false, shouldTouch: false })
  }

  return {
    unit,
    unitLabel: unit === UNITS.KM ? 'km' : 'mi',
    sliderConfig,
    displayUnitValue,
    liveMeters: radius,
    committedMeters: radiusCommitted,
    toMeters,
    fromMeters,
    onSliderChange,
    onSliderCommit,
    resetRadius,
    setUnit,
  }
}
